<script >
import { use } from '@fegobull/webapi';
import dayjs from 'dayjs';
import { computed, ref, watch } from 'vue-demi';
import { getCurrencySymbol } from '../../utils/currency';
import { getCurrentPriceLabelByLang, getStockNameByLang, useFegoPageInfo } from '../common';
import locales from '../locales';
const __sfc_main = {};
__sfc_main.props = {
  // 当前操作的场次（用于展示当前价格等）
  session: {
    type: Object,
    default: null
  },
  // 当前用户记录（用于回填）
  record: {
    type: Object,
    default: null
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const emit = __ctx.emit;
  const props = __props;
  const {
    useI18n
  } = use;
  const $t = useI18n(locales);
  const {
    isCN,
    isHK,
    isEN
  } = useFegoPageInfo();
  const price = ref('');
  const reason = ref('');
  const MAX_PRICE = 99999.99;
  const MAX_REASON_LEN = 300;

  // 价格规范化：只允许数字与一个小数点，最多 2 位小数，并限制最大值
  const normalizePrice = raw => {
    const input = String(raw ?? '');
    // 过滤掉非数字/小数点（避免 number 类型输入 e/E/-/+ 等）
    let v = input.replace(/[^\d.]/g, '');
    // 只保留第一个小数点
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    }
    // 限制最多两位小数
    if (firstDot !== -1) {
      const [i, d = ''] = v.split('.');
      v = `${i}.${d.slice(0, 2)}`;
    }
    // 限制最大值：99999.99
    const n = Number(v);
    if (Number.isFinite(n) && n > MAX_PRICE) {
      return String(MAX_PRICE);
    }
    return v;
  };
  const closePriceText = computed(() => String($t('closePrice')));

  // 结算日文案：使用场次 settle_time（YYYYMMDD）渲染为 “M月D日收盘价 / M月D日收盤價 / MMM D Close Price”
  const settleLabel = computed(() => {
    const raw = props.session?.settle_time;
    const s = raw != null ? String(raw) : '';
    const d = dayjs(s, 'YYYYMMDD', true);
    if (!d.isValid()) return closePriceText.value;
    const dateLabel = isEN.value ? d.format('MMM D') : d.format('M月D日');
    return isEN.value ? `${dateLabel} ${closePriceText.value}` : `${dateLabel}${closePriceText.value}`;
  });
  const stockName = computed(() => {
    return getStockNameByLang(props.session?.stockInfo, {
      isCN: isCN.value,
      isHK: isHK.value,
      isEN: isEN.value
    }, '-');
  });

  // 币种符号：避免模板里重复调用
  const currencySymbol = computed(() => getCurrencySymbol(props.session?.stockInfo?.currencyCode));
  const titleText = computed(() => {
    const action = String($t('predictAction'));
    return isEN.value ? `${action} ${stockName.value}` : `${action}${stockName.value}`;
  });
  const currentPriceLabel = computed(() => getCurrentPriceLabelByLang(stockName.value, {
    isCN: isCN.value,
    isHK: isHK.value,
    isEN: isEN.value
  }));

  // 预测值相对现价提示：高于/低于/等于现价 x%
  const compareTip = computed(() => {
    const currentRaw = props.session?.quote?.nominalPrice;
    const current = Number(currentRaw);
    if (!Number.isFinite(current) || current <= 0) return '';
    const guessStr = String(price.value || '').trim();
    if (!guessStr) return '';
    const guess = Number(guessStr);
    if (!Number.isFinite(guess) || guess <= 0) return '';
    const diff = guess - current;
    // 认为极小差值为等于
    if (Math.abs(diff) < 1e-9) return '';
    const pct = Math.abs(diff / current) * 100;
    // 比例过小（小于 0.01%）不展示，避免出现“0.00%”等无意义文案
    if (pct < 0.01) return '';
    const pctStr = pct.toFixed(2).replace(/\.0$/, '');
    const prefix = diff > 0 ? String($t('higherThanCurrent')) : String($t('lowerThanCurrent'));
    return `${prefix}${pctStr}%`;
  });

  // 是否可提交：价格及理由都需要填写（按当前需求）
  const canSubmit = computed(() => {
    const p = String(price.value || '').trim();
    const r = String(reason.value || '').trim();
    // 校验数字 - 两位小数 & 正数
    if (!/^\d+(\.\d{1,2})?$/.test(p)) return false;
    const pn = Number(p);
    if (pn <= 0) return false;
    if (pn > MAX_PRICE) return false;
    if (!r) return false;
    return true;
  });

  // 回填/重置数据：根据 record 回填；record 为空时清空（用于首次预测）
  const syncFromProps = () => {
    price.value = props.record?.guess_price != null ? normalizePrice(props.record.guess_price) : '';
    reason.value = props.record?.reason != null ? String(props.record.reason) : '';
  };
  watch(() => [props.session, props.record], () => {
    syncFromProps();
  }, {
    immediate: true
  });

  // 理由输入限制：最多 300 字
  const onReasonInput = e => {
    const raw = String(e?.target?.value ?? '');
    if (raw.length <= MAX_REASON_LEN) {
      reason.value = raw;
      return;
    }
    reason.value = raw.slice(0, MAX_REASON_LEN);
  };

  // 价格输入限制：最多两位小数；并在 canSubmit 里统一校验必须 >0
  const onPriceInput = e => {
    price.value = normalizePrice(e?.target?.value);
  };

  // 价格输入：禁止输入科学计数法等非法字符（例如 e/E）
  const onPriceKeydown = e => {
    const k = String(e?.key ?? '');
    // 说明：type=number 在部分浏览器会允许输入 e/E/+/-（科学计数法），这里统一禁用
    if (k === 'e' || k === 'E' || k === '+' || k === '-') {
      e.preventDefault?.();
    }
  };

  // 失焦时再做一次兜底（处理某些浏览器自动补值/粘贴场景）
  const onPriceBlur = () => {
    price.value = normalizePrice(price.value);
  };
  const handleSubmit = () => {
    if (!canSubmit.value) return;

    // 仅收集输入并向上抛出，由父组件决定何时调用接口
    emit('submit', {
      guessPrice: String(price.value).trim(),
      reason: String(reason.value).trim()
    });
  };
  const isAllowModify = computed(() => {
    return Number(props.session?.modify_cost || 0) !== -1; //eslint-disable-line @typescript-eslint/no-magic-numbers,@typescript-eslint/no-unnecessary-condition
  });
  return {
    props,
    $t,
    isEN,
    price,
    reason,
    MAX_REASON_LEN,
    settleLabel,
    currencySymbol,
    titleText,
    currentPriceLabel,
    compareTip,
    canSubmit,
    onReasonInput,
    onPriceInput,
    onPriceKeydown,
    onPriceBlur,
    handleSubmit,
    isAllowModify
  };
};
export default __sfc_main;
</script>

<template>
    <div class="_common-guess_stock-form">
        <div class="title">{{ titleText }}</div>
        <div class="price-container">
            <div class="price-item user-price">
                <div class="price-item-label-container">
                    <span class="price-item-label" :class="{ isEN: isEN }">{{ settleLabel }}</span>
                    <div v-if="compareTip" class="compare-tip" :class="{ isEN: isEN }">{{ compareTip }}</div>
                </div>
                <input
                    v-model="price"
                    class="price-item-input"
                    type="text"
                    inputmode="decimal"
                    pattern="^\d+(\.\d{0,2})?$"
                    @input="onPriceInput"
                    @keydown="onPriceKeydown"
                    @blur="onPriceBlur"
                    :placeholder="$t('pricePlaceholder')"
                    name="guessPrice"
                />
                <span class="price-item-dollar">{{ currencySymbol }}</span>
            </div>
            <div class="price-item current-price">
                <div class="price-item-label-container">
                    <span class="price-item-label">{{ currentPriceLabel }}</span>
                </div>
                <span class="price-item-value">{{
                    props.session.quote ? props.session.quote.nominalPrice : '--'
                }}</span>
                <span class="price-item-dollar">{{ currencySymbol }}</span>
            </div>
        </div>

        <div class="reason-container">
            <div class="title">{{ $t('reasonTitle') }}</div>
            <div class="reason-wrapper">
                <textarea
                    v-model="reason"
                    class="reason-textarea"
                    :maxlength="MAX_REASON_LEN"
                    @input="onReasonInput"
                    :placeholder="$t('reasonPlaceholder')"
                />
                <div class="reason-counter">{{ reason.length }}/{{ MAX_REASON_LEN }}</div>
            </div>
        </div>

        <button class="submit-btn" :class="{ disabled: !canSubmit }" :disabled="!canSubmit" @click="handleSubmit">
            {{ $t('submit') }}
        </button>
        <div class="disable-modify-tips" v-if="!isAllowModify">{{ $t('disableModifyTips') }}</div>
    </div>
</template>

<style lang="less">
._common-guess_stock-form {
    padding: 0.8rem 0.36rem 0.66rem 0.36rem;
    background-color: #fff;

    .price-container {
        margin-top: 0.3rem;
        display: flex;
        // justify-content: space-between;
    }

    .price-item {
        display: flex;
        flex-direction: column;
        position: relative;
        font-family: FZLanTingHei-DB-GBK;
        font-size: 0.24rem;
        color: #333;
        width: 2.9rem;
        height: 1.6rem;
        border-radius: 0.16rem;
        padding-top: 0.54rem;
        padding-left: 0.22rem;
    }

    .price-item-label {
        @rate: 10;
        &-container {
            display: flex;
            height: 0.32rem * @rate;
            position: absolute;
            top: 0.32rem;
            left: 0.22rem;
            transform-origin: left top;
            transform: scale((1 / @rate));
            .compare-tip {
                font-size: 0.18rem * @rate;
                height: .28rem * @rate;
                display: flex;
                align-items: center;
                font-family: FZLanTingHeiS-DB1-GBK;
                font-weight: 400;
                color: #fff;
                background-color: #ff6900;
                padding: 0 0.06rem * @rate;
                border-radius: 0.08rem * @rate;
                white-space: nowrap;
                margin-left: 0.08rem * @rate;
                &.isEN {
                    letter-spacing: -0.01em;
                    font-size: 0.16rem * @rate;
                }
            }
        }

        font-size: 0.20rem * @rate;
        line-height: 0.32rem * @rate;
        font-family: FZLanTingHeiS-DB1-GBK;
        white-space: nowrap;
        &.isEN {
            letter-spacing: -0.01em;
            font-size: 0.18rem * @rate;
            margin-right: 0.1rem;
        }

    }

    .price-item-input {
        margin-top: 0.1rem;
        width: 100%;
        box-sizing: border-box;
        border: none;
        outline: none;
        padding: 0;
        background: transparent;
        font-size: 0.44rem;
        height: 0.44rem;
        font-weight: 700;
        font-family: 'BR Hendrix';
        line-height: 1;
        color: inherit;
        appearance: textfield;
    }

    // placeholder 样式：不同浏览器需要不同的伪类，且 Firefox 默认有透明度
    .price-item-input::placeholder,
    .price-item-input::-webkit-input-placeholder,
    .price-item-input::-moz-placeholder,
    .price-item-input:-ms-input-placeholder {
        font-size: 0.36rem !important;
        color: #989898 !important;
        font-weight: 400;
        opacity: 1;
    }

    // 隐藏 number 输入框的上下箭头（兼容主流浏览器）
    .price-item-input::-webkit-outer-spin-button,
    .price-item-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .price-item-value {
        margin-top: 0.1rem;
        font-size: 0.44rem;
        font-weight: 700;
        font-family: 'BR Hendrix';
        line-height: 1;
    }

    .price-item-dollar {
        font-size: 0.26rem;
        font-family: 'BR Hendrix';
        line-height: 1;
        font-weight: 600;
    }

    .current-price {
        border: 0.01rem solid #ff8b0d;
        background: linear-gradient(110deg, #fff5e2 3.77%, #fff0d2 90.69%);
        color: #262626;
        margin-left: 0.16rem;
    }

    .user-price {
        border: 0.01rem solid #ffe9c0;
        background: #fff6f0;
        color: #ff7404;
        width: 3.6rem;
    }

    .title {
        font-size: 0.42rem;
        color: #121212;
        line-height: 0.54rem;
        font-family: FZLanTingHei-DB-BIG5;
    }

    .reason-textarea {
        margin-top: 0.3rem;
        width: 100%;
        height: 2rem;
        background-color: #f9f9f9;
        border-radius: 0.16rem;
        padding: 0.3rem;
        font-size: 0.26rem;
        color: #989898;
        line-height: 0.32rem;
        outline: none;
        border: 0.01rem solid #989898;
        resize: none;
    }

    .reason-container {
        margin-top: 0.36rem;
    }

    .reason-wrapper {
        position: relative;
    }

    .reason-counter {
        position: absolute;
        right: 0.2rem;
        bottom: 0.16rem;
        font-size: 0.24rem;
        color: #989898;
        line-height: 1;
        pointer-events: none;
    }

    .submit-btn {
        width: 5.4rem;
        height: 1rem;
        border-radius: 0.91rem;
        border: 0.02rem solid #fff;
        background: linear-gradient(93deg, #ff930f -6.59%, #ff6900 88.73%);
        font-size: 0.36rem;
        color: #fff;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0.5rem auto 0;

        cursor: pointer;

        &.disabled {
            background: linear-gradient(0deg, #989898 0%, #989898 100%),
                linear-gradient(97deg, #575757 -5.74%, #282828 25.86%, #282828 86.62%, #575757 105.61%),
                linear-gradient(93deg, #ff930f -6.59%, #ff6900 88.73%);
            pointer-events: none;
        }
    }

    .disable-modify-tips {
        color: #888;
        text-align: center;
        font-size: 0.2rem;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        margin-top: 0.1rem;
    }
}
</style>
